/**
 * The fixed document-list option catalog of the Bauwerksbuch report.
 *
 * The document list is per-report content only: it has no settings catalog entry
 * and is not configurable per tenant, so the option set is hardcoded here —
 * mirroring the backend `Report::documentListOptions()`, which is the authority
 * for the keys, the German labels and the order the renderer prints them in.
 *
 * "Keine" deliberately carries **no** `specification` key at all, not an empty
 * one, so the stored content shape matches the seeded snapshot byte for byte.
 */

export interface DocumentListOptionDefinition {
  key: string;
  label: string;
}

export interface DocumentListOption {
  key: string;
  label: string;
  checked: boolean;
  specification?: string;
}

/** The key of the option that carries no free-text specification. */
export const DOCUMENT_LIST_NONE = "keine";

export const DOCUMENT_LIST_OPTIONS: DocumentListOptionDefinition[] = [
  { key: DOCUMENT_LIST_NONE, label: "Keine" },
  { key: "neubau", label: "Neubau" },
  { key: "umbau", label: "Umbau" },
  { key: "zubau", label: "Zubau" },
  {
    key: "envelope_change",
    label: "Änderung Gebäudehülle > 25% der gesamten Oberfläche"
  },
  {
    key: "envelope_repair",
    label: "Instandsetzung Gebäudehülle > 25% der gesamten Oberfläche"
  }
];

/** The catalog as unchecked per-report content; a fresh list on every call. */
export function defaultDocumentList(): DocumentListOption[] {
  return DOCUMENT_LIST_OPTIONS.map(option => entryOf(option));
}

/**
 * A persisted payload normalized against the catalog.
 *
 * The catalog — not the payload — fixes the option set, its order and its
 * labels, so a partial, reordered, stale or malformed value still yields the
 * exact content shape. Unknown options are dropped rather than carried along.
 */
export function documentListFrom(value: unknown): DocumentListOption[] {
  const stored = Array.isArray(value) ? (value as unknown[]) : [];

  return DOCUMENT_LIST_OPTIONS.map(option =>
    entryOf(option, stored.find(entry => keyOf(entry) === option.key))
  );
}

function entryOf(
  option: DocumentListOptionDefinition,
  stored?: unknown
): DocumentListOption {
  const persisted = (stored ?? {}) as Partial<DocumentListOption>;

  const entry: DocumentListOption = {
    key: option.key,
    label: option.label,
    checked: persisted.checked === true
  };

  if (option.key !== DOCUMENT_LIST_NONE) {
    entry.specification =
      typeof persisted.specification === "string" ? persisted.specification : "";
  }

  return entry;
}

function keyOf(entry: unknown): string | undefined {
  return (entry as Partial<DocumentListOption> | undefined)?.key;
}
